import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  setTargetHours as apiSetTargetHours,
  updateProfile,
  useCurrentUserProfile,
} from '@busy/api';
import { Panel, useToast } from '@busy/ui';
import { useAuth } from '../store/auth';
import { useUI } from '../store/ui';
import { useT } from '../lib/i18n';
import {
  YEARS_OPTIONS,
  yearsLabelKey,
  isCanonicalYears,
  missingRequired,
} from '../lib/profileFields';
import { ClientsCsvPanel } from '../components/settings/ClientsCsvPanel';
import { TextTemplatesPanel } from '../components/settings/TextTemplatesPanel';
import { NotificationsPanel } from '../components/settings/NotificationsPanel';
import { DailyLessonTimePanel } from '../components/settings/DailyLessonTimePanel';
import { CutTimerEditorPanel } from '../components/settings/CutTimerEditorPanel';
import { PrivacyDataPanel } from '../components/settings/PrivacyDataPanel';

// Settings layout matches the demo's `.settings-grid`:
//   - Desktop: 240px sticky sidebar + main panel (one active at a time)
//   - Mobile: horizontally-scrolling pill row at the top, panels stack normally
//
// Sections marked `comingSoon` render a placeholder explaining what'll land there
// instead of being hidden — gives the user a sense of the v1.1 / Week 2 roadmap.

type SectionKey =
  | 'profile'
  | 'lessonprefs'
  | 'integrations'
  | 'cuttimer'
  | 'texttemplates'
  | 'clients'
  | 'subscription'
  | 'privacy';

type SectionDef = {
  key: SectionKey;
  // Translation key for the sidebar label.
  labelKey: string;
  icon: 'user' | 'target' | 'link' | 'scissors' | 'message' | 'users' | 'credit-card' | 'shield';
  // ETA stays as a translation key (covers v1.1 / Week 2 / Week 4 / etc.)
  comingSoon?: { copyKey: string; etaKey: string };
};

const SECTIONS: readonly SectionDef[] = [
  { key: 'profile', labelKey: 'settings.section.profile', icon: 'user' },
  { key: 'lessonprefs', labelKey: 'settings.section.lessonprefs', icon: 'target' },
  // Appearance sidebar entry retired — its theme + helper-visibility
  // controls live on Profile (above Notifications) now.
  // Booking integrations — Coming Soon (v1.1). The IntegrationsPanel's
  // waitlist content stays on file (vote-for-platform UI) but the
  // section now renders the standard Coming Soon placeholder instead.
  {
    key: 'integrations',
    labelKey: 'settings.section.integrations',
    icon: 'link',
    comingSoon: { copyKey: 'settings.cs.integrations', etaKey: 'settings.cs.eta.v1.1' },
  },
  { key: 'cuttimer', labelKey: 'settings.section.cuttimer', icon: 'scissors' },
  { key: 'texttemplates', labelKey: 'settings.section.texttemplates', icon: 'message' },
  { key: 'clients', labelKey: 'settings.section.clients', icon: 'users' },
  // Notifications sidebar entry retired — notification toggles now
  // live at the bottom of Profile (right above sign-out) and the
  // daily-lesson-time picker moved to Lesson Preferences.
  {
    key: 'subscription',
    labelKey: 'settings.section.subscription',
    icon: 'credit-card',
    comingSoon: { copyKey: 'settings.cs.subscription', etaKey: 'settings.cs.eta.week5' },
  },
  { key: 'privacy', labelKey: 'settings.section.privacy', icon: 'shield' },
] as const;

const GOAL_OPTIONS: Array<{ id: string; labelKey: string; subKey: string }> = [
  { id: 'retention',         labelKey: 'settings.prefs.goal.retention',
                              subKey: 'settings.prefs.goal.retentionSub' },
  { id: 'pricing',           labelKey: 'settings.prefs.goal.pricing',
                              subKey: 'settings.prefs.goal.pricingSub' },
  { id: 'time-management',   labelKey: 'settings.prefs.goal.timeManagement',
                              subKey: 'settings.prefs.goal.timeManagementSub' },
  { id: 'skill',             labelKey: 'settings.prefs.goal.skill',
                              subKey: 'settings.prefs.goal.skillSub' },
  { id: 'marketing',         labelKey: 'settings.prefs.goal.marketing',
                              subKey: 'settings.prefs.goal.marketingSub' },
  { id: 'customer-service',  labelKey: 'settings.prefs.goal.customerService',
                              subKey: 'settings.prefs.goal.customerServiceSub' },
];

const INTENSITY_OPTIONS: Array<{ id: 'gentle' | 'standard' | 'intense'; labelKey: string; subKey: string }> = [
  { id: 'gentle',   labelKey: 'settings.prefs.intensity.gentle',
                    subKey: 'settings.prefs.intensity.gentleSub' },
  { id: 'standard', labelKey: 'settings.prefs.intensity.standard',
                    subKey: 'settings.prefs.intensity.standardSub' },
  { id: 'intense',  labelKey: 'settings.prefs.intensity.intense',
                    subKey: 'settings.prefs.intensity.intenseSub' },
];

export function Settings() {
  const profile = useCurrentUserProfile();
  const navigate = useNavigate();
  const signOut = useAuth((s) => s.signOut);
  const theme = useUI((s) => s.theme);
  const setTheme = useUI((s) => s.setTheme);
  const language = useUI((s) => s.language);
  const setLanguage = useUI((s) => s.setLanguage);
  const toast = useToast();
  const t = useT();

  // Initial section can be driven by `?section=lessonprefs` etc. so deep-links
  // (e.g. the goals chips on Today) land on the right panel without an extra
  // tap. Falls back to 'profile' for any unknown / missing value.
  const [searchParams] = useSearchParams();
  const initialSection: SectionKey = (() => {
    const raw = searchParams.get('section') as SectionKey | null;
    return raw && SECTIONS.some((s) => s.key === raw) ? raw : 'profile';
  })();
  const [active, setActive] = useState<SectionKey>(initialSection);

  // Optional `?focus=targetHours` deep-link from the weekly target
  // reminder banner on Today. After the profile panel renders we
  // scroll the target hours field into view + pulse-highlight it.
  const focusParam = searchParams.get('focus');
  const targetHoursRef = useRef<HTMLDivElement | null>(null);
  const [focusFlash, setFocusFlash] = useState(false);
  useEffect(() => {
    if (focusParam !== 'targetHours') return;
    if (active !== 'profile') return;
    if (profile.loading) return;
    // Wait for the profile panel to mount, then scroll. requestAnimationFrame
    // is enough for the DOM to settle without a brittle setTimeout.
    const raf = requestAnimationFrame(() => {
      targetHoursRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setFocusFlash(true);
      // Drop the flash after the highlight animation completes.
      const timer = setTimeout(() => setFocusFlash(false), 2400);
      return () => clearTimeout(timer);
    });
    return () => cancelAnimationFrame(raf);
  }, [focusParam, active, profile.loading]);

  // Profile form state — initialized from profile when it loads
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [shopName, setShopName] = useState('');
  const [zip, setZip] = useState('');
  const [phone, setPhone] = useState('');
  const [yearsCutting, setYearsCutting] = useState('');
  const [targetHoursPerWeek, setTargetHoursPerWeek] = useState<number>(40);
  // Days-per-week pairs with hours to drive honest per-day math
  // (daily target = hours / days). Defaults to 5 (most common barber
  // schedule); the migration backfilled the same.
  const [targetDaysPerWeek, setTargetDaysPerWeek] = useState<number>(5);
  // What counts as a "fully utilized" hour for THIS barber, in 10%
  // steps from 10% to 100%. Defaults to 100% (legacy). Drives the
  // capacity heatmap shading; no history — changes recolor live.
  const [hourlyTargetPct, setHourlyTargetPct] = useState<number>(100);
  const [goals, setGoals] = useState<string[]>([]);
  const [intensity, setIntensity] = useState<'gentle' | 'standard' | 'intense' | ''>('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);

  useEffect(() => {
    if (!profile.data) return;
    setFirstName(profile.data.first_name ?? '');
    setLastName(profile.data.last_name ?? '');
    setShopName(profile.data.shop_name ?? '');
    setZip(profile.data.zip ?? '');
    setPhone(profile.data.phone ?? '');
    setYearsCutting(profile.data.years_cutting ?? '');
    setTargetHoursPerWeek(profile.data.target_hours_per_week ?? 40);
    setTargetDaysPerWeek(profile.data.target_days_per_week ?? 5);
    setHourlyTargetPct(profile.data.hourly_target_pct ?? 100);
    setGoals(profile.data.goals ?? []);
    setIntensity(profile.data.intensity ?? '');
  }, [profile.data]);

  async function handleSaveProfile() {
    const missing = missingRequired({
      first_name: firstName.trim() || null,
      zip: zip.trim() || null,
      years_cutting: yearsCutting || null,
    });
    if (missing.length > 0) {
      const fieldNames = missing.map((f) => t(`profile.field.${f}`)).join(', ');
      toast(t('profile.missingRequired', { fields: fieldNames }), 'error');
      return;
    }
    setSavingProfile(true);
    // Profile fields go through updateProfile; target hours uses the
    // dedicated helper so the change lands in user_target_hours history
    // (effective from this week's Monday) and the cache stays in sync.
    const profileRes = await updateProfile({
      first_name: firstName.trim(),
      last_name: lastName.trim() || null,
      shop_name: shopName.trim() || null,
      zip: zip.trim(),
      phone: phone.trim() || null,
      years_cutting: yearsCutting,
      hourly_target_pct: hourlyTargetPct,
    });
    if (profileRes.error) {
      setSavingProfile(false);
      toast(t('settings.profile.saveError', { message: profileRes.error.message }), 'error');
      return;
    }
    // Fire setTargetHours when the value actually differs from what's
    // stored. The previous comparison used `?? targetHoursPerWeek` as
    // the fallback for null, which silently treated "null in DB" as
    // "matches the form's default 40" — the API call was skipped, the
    // capacity tile kept showing "Set your target," and the user
    // thought save was broken. Comparing against the raw stored value
    // (including null) means the very first save always lands.
    // Fire setTargetHours when EITHER hours OR days changed. The
    // helper writes both fields to users.target_* and inserts a new
    // user_target_hours row keyed on this week's Monday so the change
    // applies going forward but not retroactively.
    const storedTarget = profile.data?.target_hours_per_week ?? null;
    const storedDays = profile.data?.target_days_per_week ?? null;
    if (storedTarget !== targetHoursPerWeek || storedDays !== targetDaysPerWeek) {
      const targetRes = await apiSetTargetHours(targetHoursPerWeek, targetDaysPerWeek);
      if (targetRes.error) {
        setSavingProfile(false);
        toast(t('settings.profile.saveError', { message: targetRes.error.message }), 'error');
        return;
      }
    }
    setSavingProfile(false);
    toast(t('settings.profile.saved'), 'success');
  }

  async function handleSavePrefs() {
    setSavingPrefs(true);
    const { error } = await updateProfile({ goals, intensity: intensity || null });
    setSavingPrefs(false);
    if (error) toast(t('settings.profile.saveError', { message: error.message }), 'error');
    else toast(t('settings.prefs.saved'), 'success');
  }

  function toggleGoal(id: string) {
    setGoals((cur) => (cur.includes(id) ? cur.filter((g) => g !== id) : [...cur, id]));
  }

  async function handleSignOut() {
    if (!confirm(t('menu.signOutConfirm'))) return;
    await signOut();
    navigate('/sign-in', { replace: true });
  }

  if (profile.loading) {
    return (
      <div className="max-w-page mx-auto p-4 md:p-6 space-y-4">
        <div className="h-10 w-40 bg-bg-alt rounded animate-pulse" />
        <div className="h-40 rounded-2xl bg-bg-alt animate-pulse" />
      </div>
    );
  }

  const activeSection = SECTIONS.find((s) => s.key === active) ?? SECTIONS[0];

  return (
    <div className="max-w-page mx-auto p-4 md:p-6 space-y-5 pb-24">
      <header>
        <h1 className="font-display text-4xl md:text-[42px] text-navy-900 dark:text-gold-500 leading-[1.05]">
          {t('settings.title')}
        </h1>
        <p className="text-[15px] text-ink-500 mt-1.5">{profile.data?.email}</p>
      </header>

      <div className="md:grid md:grid-cols-[240px_1fr] md:gap-7 md:items-start">
        {/* Sidebar nav — sticky on desktop, horizontal scroll on mobile */}
        <nav
          aria-label={t('settings.aria')}
          className="md:sticky md:top-24 md:flex md:flex-col md:gap-1 -mx-4 px-4 md:mx-0 md:px-0 overflow-x-auto md:overflow-visible mb-4 md:mb-0"
          style={{ scrollbarWidth: 'none' }}
        >
          <ul className="flex md:flex-col gap-1 md:gap-1 pb-2 md:pb-0">
            {SECTIONS.map((s) => {
              const isActive = active === s.key;
              return (
                <li key={s.key} className="shrink-0">
                  <button
                    type="button"
                    onClick={() => setActive(s.key)}
                    className={`w-full text-left whitespace-nowrap inline-flex items-center gap-2.5 px-3.5 py-2.5 rounded-[10px] text-sm font-semibold transition-colors ${
                      isActive
                        ? 'bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900'
                        : 'bg-transparent text-ink-700 hover:bg-bg-alt'
                    }`}
                  >
                    <SidebarIcon name={s.icon} className="w-4 h-4" />
                    <span>{t(s.labelKey)}</span>
                    {s.comingSoon && (
                      <span
                        className={`ml-auto text-[9px] font-extrabold tracking-[0.1em] uppercase px-1.5 py-0.5 rounded-full ${
                          isActive
                            ? 'bg-white/15 text-white/85 dark:bg-navy-900/15 dark:text-navy-900/70'
                            : 'bg-bg-alt text-ink-500 border border-line'
                        }`}
                      >
                        {t(s.comingSoon.etaKey)}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Active panel */}
        <div className="space-y-4">
          {activeSection.comingSoon && (
            <ComingSoonPanel section={activeSection} />
          )}

          {active === 'profile' && (
            <>
              <Panel title={t('settings.profile.title')} sub={t('settings.profile.sub')}>
                <div className="space-y-3">
                  <Field
                    label={t('settings.profile.firstName')}
                    value={firstName}
                    onChange={setFirstName}
                    placeholder="Sam"
                    required
                  />
                  <Field
                    label={t('settings.profile.lastName')}
                    value={lastName}
                    onChange={setLastName}
                    placeholder="Garcia"
                  />
                  <Field
                    label={t('settings.profile.shopName')}
                    value={shopName}
                    onChange={setShopName}
                    placeholder="Super Barber"
                  />
                  <Field
                    label={t('settings.profile.zip')}
                    value={zip}
                    onChange={(v) => setZip(v.replace(/\D/g, '').slice(0, 5))}
                    placeholder="77002"
                    inputMode="numeric"
                    help={t('settings.profile.zipHelp')}
                    required
                  />
                  <Field
                    label={t('settings.profile.phone')}
                    value={phone}
                    onChange={setPhone}
                    type="tel"
                    placeholder="(555) 123-4567"
                    help={t('settings.profile.phoneHelp')}
                  />
                  <YearsSelect
                    label={t('settings.profile.years')}
                    value={yearsCutting}
                    onChange={setYearsCutting}
                    help={t('settings.profile.yearsHelp')}
                  />
                  {/* Language — moved here from Appearance per the
                      consolidation: it's tied more to the barber's own
                      identity than to the device's look-and-feel.
                      Sits above target hours so the save button at the
                      bottom commits the whole profile in one tap. */}
                  <LanguageField
                    value={language}
                    onChange={setLanguage}
                    label={t('settings.appearance.langHeader')}
                    help={t('settings.appearance.langSub')}
                  />
                  <div
                    ref={targetHoursRef}
                    className={`rounded-xl transition-shadow ${
                      focusFlash
                        ? 'ring-2 ring-gold-500 ring-offset-2 ring-offset-bg shadow-[0_0_0_4px_rgba(212,169,66,0.18)]'
                        : ''
                    }`}
                  >
                    <TargetHoursField
                      value={targetHoursPerWeek}
                      onChange={setTargetHoursPerWeek}
                      label={t('settings.profile.targetHours')}
                      help={t('settings.profile.targetHoursHelp')}
                      customLabel={t('onb.targetHours.customLabel')}
                      unit={t('onb.targetHours.unit')}
                    />
                  </div>
                  {/* Days per week — pairs with target hours so daily
                      math is honest (8h × 5 days, not 5.7h × 7 days). */}
                  <TargetDaysField
                    value={targetDaysPerWeek}
                    onChange={setTargetDaysPerWeek}
                    label={t('settings.profile.targetDays')}
                    help={t('settings.profile.targetDaysHelp', {
                      perDay: (targetHoursPerWeek / targetDaysPerWeek).toFixed(1),
                    })}
                  />
                  {/* Hourly target percentage — drives the capacity
                      heatmap's color buckets. 100% means a "full hour"
                      is 60 minutes of cuts; 80% means 48 minutes. */}
                  <HourlyTargetField
                    value={hourlyTargetPct}
                    onChange={setHourlyTargetPct}
                    label={t('settings.profile.hourlyTarget')}
                    help={t('settings.profile.hourlyTargetHelp', {
                      minutes: Math.round(60 * (hourlyTargetPct / 100)),
                    })}
                  />
                  <ReminderToggle />
                  <button
                    type="button"
                    onClick={handleSaveProfile}
                    disabled={savingProfile}
                    className="w-full h-11 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-semibold disabled:opacity-50"
                  >
                    {savingProfile ? t('settings.profile.saving') : t('settings.profile.save')}
                  </button>
                </div>
              </Panel>

              {/* Appearance — theme + helper-visibility toggle. Used to
                  live in its own sidebar entry but it's just two
                  toggles, and barbers expect everything customizable
                  about their account to live one tap away on Profile. */}
              <Panel title={t('settings.appearance.title')} sub={t('settings.appearance.sub')}>
                <div>
                  <div className="text-xs font-semibold text-ink-500 mb-2">
                    {t('settings.appearance.themeHeader')}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <SegmentButton
                      active={theme === 'light'}
                      onClick={() => setTheme('light')}
                      label={t('settings.appearance.themeLight')}
                    />
                    <SegmentButton
                      active={theme === 'dark'}
                      onClick={() => setTheme('dark')}
                      label={t('settings.appearance.themeDark')}
                    />
                  </div>
                </div>
                <HelperVisibilityRow />
              </Panel>

              {/* Notification preferences — daily lesson nudge, weekly
                  recap email, at-risk alerts. Lived in its own sidebar
                  entry until we noticed every other toggle in the app
                  is already on the Profile screen; the standalone tab
                  was a click for nothing. Daily-lesson-time picker
                  moved over to Lesson Preferences (it's about WHEN
                  lessons arrive, which is a lesson preference). */}
              <Panel title={t('settings.notif.title')} sub={t('settings.notif.sub')}>
                <NotificationsPanel />
              </Panel>

              {/* Account panel — moved here from the Appearance tab so the
                  email + sign-out controls sit alongside the rest of the
                  user's identity / account-level settings. */}
              <Panel title={t('settings.account.title')} sub="">
                <div className="rounded-xl border border-line bg-bg-alt p-3 text-sm">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-500">
                    {t('settings.account.email')}
                  </div>
                  <div className="font-medium mt-0.5 break-all">{profile.data?.email}</div>
                </div>
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="w-full h-11 mt-3 rounded-lg border border-red-500/40 bg-surface text-red-600 dark:text-red-400 font-semibold hover:bg-red-50 dark:hover:bg-red-600/15"
                >
                  {t('settings.account.signOut')}
                </button>
              </Panel>
            </>
          )}

          {active === 'lessonprefs' && (
            <>
            <Panel title={t('settings.prefs.title')} sub={t('settings.prefs.sub')}>
              <div className="space-y-2">
                {GOAL_OPTIONS.map((g) => {
                  const isActive = goals.includes(g.id);
                  const atLimit = goals.length >= 2 && !isActive;
                  return (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => toggleGoal(g.id)}
                      disabled={atLimit}
                      className={`w-full text-left rounded-xl border p-3 transition-colors ${
                        isActive
                          ? 'border-navy-900 dark:border-gold-500 bg-navy-900/5 dark:bg-gold-500/10'
                          : 'border-line bg-surface hover:border-navy-900/40 dark:hover:border-gold-500/40'
                      } ${atLimit ? 'opacity-40 cursor-not-allowed' : ''}`}
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className={`w-5 h-5 rounded grid place-items-center border-2 shrink-0 ${
                            isActive
                              ? 'bg-navy-900 dark:bg-gold-500 border-navy-900 dark:border-gold-500'
                              : 'border-line'
                          }`}
                        >
                          {isActive && <CheckIcon className="w-3 h-3 text-white dark:text-navy-900" />}
                        </span>
                        <div className="flex-1">
                          <strong className="block">{t(g.labelKey)}</strong>
                          <span className="text-xs text-ink-500">{t(g.subKey)}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="mt-4">
                <div className="text-xs font-semibold text-ink-500 mb-2">
                  {t('settings.prefs.intensityHeader')}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {INTENSITY_OPTIONS.map((opt) => {
                    const isActive = intensity === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setIntensity(opt.id)}
                        className={`rounded-xl border p-3 text-left transition-colors ${
                          isActive
                            ? 'border-navy-900 dark:border-gold-500 bg-navy-900/5 dark:bg-gold-500/10'
                            : 'border-line bg-surface hover:border-navy-900/40 dark:hover:border-gold-500/40'
                        }`}
                      >
                        <strong className="text-sm block">{t(opt.labelKey)}</strong>
                        <span className="text-[11px] text-ink-500 leading-tight">{t(opt.subKey)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <button
                type="button"
                onClick={handleSavePrefs}
                disabled={savingPrefs}
                className="w-full h-11 mt-4 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-semibold disabled:opacity-50"
              >
                {savingPrefs ? t('settings.profile.saving') : t('settings.prefs.save')}
              </button>
            </Panel>

            {/* Daily lesson delivery time — moved here from the now-
                deleted Notifications tab. "When my lesson shows up" is
                a lesson preference, sits naturally with goals + intensity. */}
            <Panel title={t('settings.delivery.title')} sub={t('settings.delivery.sub')}>
              <DailyLessonTimePanel />
            </Panel>
            </>
          )}

          {/* (Integrations panel intentionally omitted — the section is
              flagged comingSoon now, so ComingSoonPanel renders above.

              Notifications panel intentionally omitted too — it now
              lives inside the Profile tab. Daily-lesson-time moved to
              Lesson Preferences. The standalone Notifications sidebar
              entry was retired with this ship.) */}

          {active === 'cuttimer' && (
            <Panel title={t('settings.cuttimer.title')} sub={t('settings.cuttimer.sub')}>
              <CutTimerEditorPanel />
            </Panel>
          )}

          {active === 'privacy' && (
            <Panel title={t('settings.privacy.title')} sub={t('settings.privacy.sub')}>
              <PrivacyDataPanel />
            </Panel>
          )}

          {active === 'clients' && (
            <Panel
              title={t('settings.clientsPanel.title')}
              sub={t('settings.clientsPanel.sub')}
            >
              <ClientsCsvPanel />
            </Panel>
          )}

          {active === 'texttemplates' && (
            <Panel
              title={t('settings.textTemplates.title')}
              sub={t('settings.textTemplates.sub')}
            >
              <TextTemplatesPanel />
            </Panel>
          )}

          {/* (Appearance panel intentionally omitted — theme + helper
              visibility moved to Profile. The standalone sidebar entry
              was retired with this ship.) */}
        </div>
      </div>
    </div>
  );
}

function ComingSoonPanel({ section }: { section: SectionDef }) {
  const t = useT();
  if (!section.comingSoon) return null;
  const eta = t(section.comingSoon.etaKey);
  return (
    <Panel
      title={t(section.labelKey)}
      sub={t('settings.cs.landsIn', { eta })}
      action={
        <span
          className="text-[10px] tracking-[0.14em] uppercase font-extrabold text-gold-500 bg-gold-500/10 py-1 pr-2 rounded-full"
          style={{ paddingLeft: 'calc(0.5rem + 0.14em)' }}
        >
          {t('settings.cs.tag', { eta })}
        </span>
      }
    >
      <p className="text-[14px] leading-relaxed text-ink-700">{t(section.comingSoon.copyKey)}</p>
    </Panel>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  inputMode,
  placeholder,
  help,
  required = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  inputMode?: 'text' | 'numeric' | 'tel' | 'email';
  placeholder?: string;
  help?: string;
  required?: boolean;
}) {
  return (
    <div>
      <FieldLabel text={label} required={required} />
      <input
        type={type}
        inputMode={inputMode}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-10 px-3 rounded-lg border border-line bg-surface text-ink-900 placeholder:text-ink-500 focus:outline-none focus:ring-2 focus:ring-navy-900 dark:focus:ring-gold-500"
      />
      {help && <p className="text-[11px] text-ink-500 mt-1 leading-snug">{help}</p>}
    </div>
  );
}

// Mirrors the convention in Onboarding.tsx — required gets a red asterisk;
// optional gets a muted "(optional)" tag.
function FieldLabel({ text, required = false }: { text: string; required?: boolean }) {
  const t = useT();
  return (
    <label className="block text-xs font-semibold text-ink-500 mb-1">
      {text}
      {required ? (
        <span className="text-red-600 ml-0.5" aria-hidden>
          *
        </span>
      ) : (
        <span className="text-ink-500 font-normal opacity-60 ml-1">
          ({t('profile.optional').toLowerCase()})
        </span>
      )}
    </label>
  );
}

// Years cutting dropdown — required, mirrors Onboarding's options. If the
// stored value is a legacy range string ('lt1', '8-12', '13+') we render it
// as a one-off "needs update" option so the user sees something meaningful
// until they re-select; saving will reject the legacy value via
// missingRequired() and force them to pick from the canonical list.
function YearsSelect({
  label,
  value,
  onChange,
  help,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  help?: string;
}) {
  const t = useT();
  const isLegacy = !!value && !isCanonicalYears(value);
  return (
    <div>
      <FieldLabel text={label} required />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 px-3 rounded-lg border border-line bg-surface text-ink-900 focus:outline-none focus:ring-2 focus:ring-navy-900 dark:focus:ring-gold-500"
      >
        <option value="">{t('profile.years.placeholder')}</option>
        {YEARS_OPTIONS.map((v) => (
          <option key={v} value={v}>
            {t(yearsLabelKey(v))}
          </option>
        ))}
        {isLegacy && (
          <option value={value} disabled>
            {value} — {t('profile.years.legacyHint')}
          </option>
        )}
      </select>
      {help && <p className="text-[11px] text-ink-500 mt-1 leading-snug">{help}</p>}
    </div>
  );
}

// Language picker — lives on the Profile tab (above target hours)
// since it's tied to the barber's identity, not the device's look-and-
// feel. Writes through to the UI store; no Save click needed because
// the user wants to see the language switch immediately.
function LanguageField({
  value,
  onChange,
  label,
  help,
}: {
  value: 'en' | 'es';
  onChange: (lang: 'en' | 'es') => void;
  label: string;
  help?: string;
}) {
  const t = useT();
  return (
    <div>
      <FieldLabel text={label} />
      {help && <p className="text-[11px] text-ink-500 mb-2 leading-snug">{help}</p>}
      <div className="grid grid-cols-2 gap-2">
        <SegmentButton
          active={value === 'en'}
          onClick={() => onChange('en')}
          label={t('settings.appearance.langEn')}
        />
        <SegmentButton
          active={value === 'es'}
          onClick={() => onChange('es')}
          label={t('settings.appearance.langEs')}
        />
      </div>
    </div>
  );
}

// Weekly reminder toggle. Reads + writes users.weekly_target_reminder_enabled
// directly so it lives outside the parent's "Save profile" flow — toggling
// is itself the action; no extra Save click needed.
function ReminderToggle() {
  const profile = useCurrentUserProfile();
  const t = useT();
  const toast = useToast();
  const enabled = profile.data?.weekly_target_reminder_enabled ?? true;
  async function toggle() {
    const next = !enabled;
    const res = await updateProfile({ weekly_target_reminder_enabled: next });
    if (res.error) toast(t('settings.profile.saveError', { message: res.error.message }), 'error');
  }
  return (
    <label className="flex items-start gap-3 rounded-xl border border-line bg-bg-alt p-3 cursor-pointer hover:border-navy-900/40 dark:hover:border-gold-500/40 transition-colors">
      <input
        type="checkbox"
        checked={enabled}
        onChange={toggle}
        className="mt-0.5 w-5 h-5 shrink-0 accent-navy-900 dark:accent-gold-500 cursor-pointer"
      />
      <div className="min-w-0">
        <strong className="block text-sm leading-tight">
          {t('settings.profile.weeklyReminder')}
        </strong>
        <p className="text-[12px] text-ink-500 mt-0.5 leading-snug">
          {t('settings.profile.weeklyReminderHelp')}
        </p>
      </div>
    </label>
  );
}

// Hourly target percentage picker — a native <select> with 10%
// increments from 10% to 100%. Drives the capacity heatmap's color
// shading by defining what counts as a "full hour" for THIS barber.
// 100% (legacy default) means 60 min of cuts per hour is "full";
// 80% means 48 min is enough; 70% = 42 min. Lowering it shifts every
// rendered hour up the gradient (no history — recolors live).
function HourlyTargetField({
  value,
  onChange,
  label,
  help,
}: {
  value: number;
  onChange: (n: number) => void;
  label: string;
  help?: string;
}) {
  return (
    <div>
      <FieldLabel text={label} />
      <select
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        className="w-full h-11 px-3 rounded-xl border-[1.5px] border-line-strong bg-surface text-ink-900 font-bold tabular-nums focus:outline-none focus:border-navy-700 dark:focus:border-gold-500"
      >
        {[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((p) => (
          <option key={p} value={p}>
            {p}%
          </option>
        ))}
      </select>
      {help && <p className="text-[11px] text-ink-500 mt-1 leading-snug">{help}</p>}
    </div>
  );
}

// Target days per week picker (1–7). Companion to TargetHoursField —
// pair gives an honest daily target (weekly hours / weekly days).
// Clamped at 7 to avoid div-by-zero math elsewhere.
function TargetDaysField({
  value,
  onChange,
  label,
  help,
}: {
  value: number;
  onChange: (n: number) => void;
  label: string;
  help?: string;
}) {
  const clamped = Math.max(1, Math.min(7, value));
  return (
    <div>
      <FieldLabel text={label} />
      <div className="grid grid-cols-7 gap-1">
        {[1, 2, 3, 4, 5, 6, 7].map((d) => {
          const selected = clamped === d;
          return (
            <button
              key={d}
              type="button"
              onClick={() => onChange(d)}
              aria-label={String(d)}
              className={`h-11 rounded-lg border-[1.5px] font-display text-base transition-all ${
                selected
                  ? 'border-navy-900 dark:border-gold-500 bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900'
                  : 'border-line-strong bg-surface text-ink-900 hover:border-navy-700 dark:hover:border-gold-400'
              }`}
            >
              {d}
            </button>
          );
        })}
      </div>
      {help && <p className="text-[11px] text-ink-500 mt-1 leading-snug">{help}</p>}
    </div>
  );
}

function TargetHoursField({
  value,
  onChange,
  label,
  help,
  customLabel,
  unit,
}: {
  value: number;
  onChange: (n: number) => void;
  label: string;
  help?: string;
  customLabel: string;
  unit: string;
}) {
  return (
    <div>
      <FieldLabel text={label} />
      <div className="grid grid-cols-4 gap-2">
        {[20, 30, 40, 50].map((h) => {
          // Exact-match selection. Values above 50 (or any other non-preset)
          // surface in the Custom input below — keeps the pills literal.
          const selected = value === h;
          const pillLabel = String(h);
          return (
            <button
              key={h}
              type="button"
              onClick={() => onChange(h)}
              className={`h-11 rounded-lg border-[1.5px] font-display text-base transition-all ${
                selected
                  ? 'border-navy-900 dark:border-gold-500 bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900'
                  : 'border-line-strong bg-surface text-ink-900 hover:border-navy-700 dark:hover:border-gold-400'
              }`}
            >
              {pillLabel}
            </button>
          );
        })}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <label className="text-[11px] text-ink-500">{customLabel}</label>
        <input
          type="number"
          min={1}
          max={100}
          value={value}
          onChange={(e) => {
            const n = Math.max(1, Math.min(100, parseInt(e.target.value, 10) || 0));
            onChange(n);
          }}
          className="w-20 h-9 px-2 rounded-lg border border-line bg-surface text-ink-900 text-center font-bold tabular-nums focus:outline-none focus:ring-2 focus:ring-navy-900 dark:focus:ring-gold-500"
        />
        <span className="text-[11px] text-ink-500">{unit}</span>
      </div>
      {help && <p className="text-[11px] text-ink-500 mt-1 leading-snug">{help}</p>}
    </div>
  );
}

function SegmentButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-10 rounded-lg font-semibold text-sm transition-colors ${
        active
          ? 'bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900'
          : 'bg-bg-alt text-ink-700 hover:bg-line'
      }`}
    >
      {label}
    </button>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function SidebarIcon({
  name,
  className,
}: {
  name: SectionDef['icon'];
  className?: string;
}) {
  const props = {
    className,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
  switch (name) {
    case 'user':
      return (
        <svg {...props}>
          <circle cx="12" cy="7" r="4" />
          <path d="M5 21v-2a7 7 0 0 1 14 0v2" />
        </svg>
      );
    case 'target':
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="10" />
          <circle cx="12" cy="12" r="6" />
          <circle cx="12" cy="12" r="2" />
        </svg>
      );
    case 'link':
      return (
        <svg {...props}>
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </svg>
      );
    case 'scissors':
      return (
        <svg {...props}>
          <circle cx="6" cy="6" r="3" />
          <circle cx="6" cy="18" r="3" />
          <line x1="20" y1="4" x2="8.12" y2="15.88" />
          <line x1="14.47" y1="14.48" x2="20" y2="20" />
          <line x1="8.12" y1="8.12" x2="12" y2="12" />
        </svg>
      );
    case 'message':
      return (
        <svg {...props}>
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      );
    case 'users':
      return (
        <svg {...props}>
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    case 'credit-card':
      return (
        <svg {...props}>
          <rect x="1" y="4" width="22" height="16" rx="2" />
          <line x1="1" y1="10" x2="23" y2="10" />
        </svg>
      );
    case 'shield':
      return (
        <svg {...props}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      );
  }
}

// Toggle for the floating Busy mascot helper. Lives at the bottom of the
// Appearance panel so experienced users can hide it when they don't want
// it in the way, and turn it back on whenever they want a refresher.
function HelperVisibilityRow() {
  const t = useT();
  const visible = useUI((s) => s.helperVisible);
  const setHelperVisible = useUI((s) => s.setHelperVisible);
  return (
    <label className="mt-4 flex items-center justify-between gap-3 rounded-2xl border border-line bg-surface px-4 py-3 cursor-pointer">
      <div className="min-w-0 flex-1">
        <strong className="block text-sm">{t('settings.appearance.helperLabel')}</strong>
        <span className="text-xs text-ink-500">{t('settings.appearance.helperSub')}</span>
      </div>
      <span
        className={`relative inline-block w-10 h-6 rounded-full transition-colors shrink-0 ${
          visible ? 'bg-navy-900 dark:bg-gold-500' : 'bg-line-strong'
        }`}
      >
        <input
          type="checkbox"
          checked={visible}
          onChange={(e) => setHelperVisible(e.target.checked)}
          className="absolute opacity-0 w-full h-full cursor-pointer"
        />
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
            visible ? 'translate-x-4' : ''
          }`}
        />
      </span>
    </label>
  );
}
